import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { useInViewport } from 'ahooks';
import type { OmitStylesHTMLAttributes } from '../../types';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const readMoreVariants = cva('rocco-read-more', {
    variants: {
        variant: {
            default:
                'transition-all duration-300 motion-reduce:transition-none',
            noTransition: '',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

export interface ReadMoreProps {
    /**
     * Whether the content is clamped (truncated) initially
     * @default true
     */
    clamped?: boolean;
    /**
     * Maximum number of lines to show when clamped
     * @default 3
     */
    maxLines?: number;
    /**
     * Maximum height in pixels (alternative to maxLines)
     */
    maxHeight?: number;
    /**
     * Whether to auto-scroll to the component when toggling
     * @default true
     */
    autoScroll?: boolean;
    /**
     * Text or ReactNode for the "read more" button
     * @default "Read more"
     */
    readMoreText?: string | React.ReactNode;
    /**
     * Text or ReactNode for the "read less" button
     * @default "Read less"
     */
    readLessText?: string | React.ReactNode;
    /**
     * Offset from the top of the viewport when scrolling
     * @default 0
     */
    scrollOffset?: number;
    /**
     * Threshold for viewport intersection detection
     * @default 0.75
     */
    scrollViewportThreshold?: number;
    /**
     * Whether to show a button to toggle (false means click on content to toggle)
     * @default true
     */
    buttonRequired?: boolean;
    /**
     * Callback when the clamped state changes
     */
    onToggle?: (isClamped: boolean) => void;
    /**
     * Custom class for the toggle button
     */
    buttonClassName?: string;
    /**
     * Custom class for the text
     */
    textClassName?: string;
    /**
     * Custom class for the component root
     */
    className?: string;
    /**
     * Whether to always show the toggle button
     * @default false
     */
    alwaysShowButton?: boolean;
    /**
     * Whether to toggle the content when clicking on the content
     * @default true
     */
    toggleOnClick?: boolean;

    /**
     * Callback when the content is toggled
     */
    callBack?: (e?: React.MouseEvent<HTMLDivElement>) => void;

    /**
     * Callback fired when the clamped element's overflow state changes.
     * Useful for callers that need to drive `alwaysShowButton` from real
     * overflow rather than guessing from outside the component.
     */
    onOverflowChange?: (isOverflowing: boolean) => void;
}

interface ReadMoreComponentProps
    extends
        OmitStylesHTMLAttributes<HTMLDivElement>,
        ReadMoreProps,
        VariantProps<typeof readMoreVariants> {}

/**
 * A component that truncates content and provides "Read more" / "Read less" buttons to expand or collapse the content.
 * Uses Tailwind's line-clamp utilities for better browser compatibility.
 * Automatically applies rich text styling for HTML content.
 */
export const ReadMore = forwardRef<HTMLDivElement, ReadMoreComponentProps>(
    ({ children, variant, className, buttonClassName, ...props }, ref) => {
        const {
            clamped: clampedProp = true,
            maxLines = 3,
            maxHeight,
            autoScroll = true,
            readMoreText = 'Read more',
            readLessText = 'Read less',
            scrollOffset = 0,
            scrollViewportThreshold = 0.75,
            buttonRequired = true,
            textClassName,
            onToggle,
            alwaysShowButton = false,
            toggleOnClick = true,
            callBack,
            onOverflowChange,
        } = props;

        const contentRef = useRef<HTMLDivElement>(null);
        const [clamped, setClamped] = useState(clampedProp);
        const lastOverflowRef = useRef<boolean | null>(null);

        useEffect(() => {
            if (!onOverflowChange) return;
            const el = contentRef.current;
            if (!el || typeof window === 'undefined') return;

            let cancelled = false;

            const measure = () => {
                if (cancelled || !contentRef.current) return;
                const node = contentRef.current;
                // When unclamped, the element has its full natural height,
                // so scrollHeight === clientHeight. Reuse the previous value
                // so the button doesn't disappear while expanded.
                if (!clamped) return;
                const overflowing = node.scrollHeight > node.clientHeight + 1;
                if (overflowing !== lastOverflowRef.current) {
                    lastOverflowRef.current = overflowing;
                    onOverflowChange(overflowing);
                }
            };

            // Wait for fonts before the first measure — Safari's
            // -webkit-line-clamp height changes once custom fonts load.
            const fonts = (document as Document & { fonts?: FontFaceSet })
                .fonts;
            if (fonts?.ready) {
                fonts.ready.then(measure).catch(measure);
            } else {
                measure();
            }

            const ro =
                typeof ResizeObserver !== 'undefined'
                    ? new ResizeObserver(measure)
                    : null;
            ro?.observe(el);
            window.addEventListener('resize', measure);

            return () => {
                cancelled = true;
                ro?.disconnect();
                window.removeEventListener('resize', measure);
            };
        }, [onOverflowChange, clamped, maxLines, maxHeight, children]);

        const [inViewport] = useInViewport(contentRef, {
            threshold: scrollViewportThreshold,
        });

        const handleToggle = (event: React.MouseEvent<Element, MouseEvent>) => {
            if (callBack) callBack();
            if (!toggleOnClick) return;
            event.stopPropagation();
            const newClampedState = !clamped;
            setClamped(newClampedState);

            // Call the onToggle callback if provided
            if (onToggle) {
                onToggle(newClampedState);
            }

            if (clamped && autoScroll) {
                const element = contentRef.current;
                if (element && !inViewport) {
                    const top = element.getBoundingClientRect().top;
                    window.scrollTo({
                        behavior: 'smooth',
                        top: top + window.scrollY - scrollOffset,
                    });
                }
            }
        };

        // Get rich text classes for proper styling
        const getRichTextClasses = () => {
            return cn(
                '[&_p:not(:last-child)]:mb-4',
                '[&_ol]:list-decimal [&_ul]:list-disc',
                '[&_ol]:pl-5 [&_ul]:pl-5',
                '[&_li]:mb-2',
                '[&_h1]:mb-4 [&_h1]:text-2xl [&_h1]:font-semibold',
                '[&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-semibold',
                '[&_h3]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold',
                '[&_h4]:mb-2 [&_h4]:font-semibold',
                '[&_a]:text-foreground [&_a]:underline',
                '[&_blockquote]:my-4 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:italic',
            );
        };

        return (
            <div ref={ref} className={className}>
                <div
                    ref={contentRef}
                    aria-expanded={!clamped}
                    className={cn(
                        readMoreVariants({ variant }),
                        clamped && maxLines && maxLines <= 6
                            ? `line-clamp-${maxLines}`
                            : '',
                        maxHeight && clamped ? 'overflow-hidden' : '',
                        !buttonRequired ? 'cursor-pointer' : '',
                        // Always apply rich text styles
                        'prose-sm prose-gray dark:prose-invert max-w-none',
                        getRichTextClasses(),
                        textClassName,
                    )}
                    style={
                        clamped
                            ? maxLines && maxLines > 6
                                ? {
                                      overflow: 'hidden',
                                      display: '-webkit-box',
                                      WebkitBoxOrient: 'vertical',
                                      WebkitLineClamp: maxLines,
                                      ...(maxHeight
                                          ? { maxHeight: `${maxHeight}px` }
                                          : {}),
                                  }
                                : maxHeight
                                  ? { maxHeight: `${maxHeight}px` }
                                  : undefined
                            : undefined
                    }
                    onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                        if (!buttonRequired) handleToggle(e);
                    }}
                >
                    {children}
                </div>

                {buttonRequired && (alwaysShowButton || !clamped) && (
                    <button
                        type="button"
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                            handleToggle(e)
                        }
                        className={cn(
                            'mt-2 bg-transparent text-foreground underline-offset-2 hover:underline md:mt-4',
                            buttonClassName,
                        )}
                    >
                        {clamped ? readMoreText : readLessText}
                    </button>
                )}
            </div>
        );
    },
);

ReadMore.displayName = 'ReadMore';
